import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { Payment as PaymentType } from "@/apis/gql/types";
import { useTenant } from "@/providers/session";
import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client";
type InitiatePaymentInput = {
  payments: PaymentType[];
  bankAccountId: string;
  bankConnectionId: string;
};

export const useInitiatePayment = ({
  config: { showToasts = true } = {},
}: {
  config?: { showToasts?: boolean };
} = {}) => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [initiatePayment, { loading, error, data }] = useMutation(
    INITIATE_PAYMENT_MUTATION,
  );

  const handleInitiatePayment = async (input: InitiatePaymentInput) => {
    let toastId: string | number | undefined;

    if (!tenantId) {
      throw new Error("Tenant ID is required");
    }

    if (showToasts) {
      toastId = toast.loading(t("generic_loading"));
    }

    try {
      const paymentIds = input.payments.map((payment) => payment.bankTxnId);
      const payments = input.payments.map((payment) => ({
        bankTxnId: payment.bankTxnId,
        amount: payment.amount,
        currency: payment.currency,
        recipientName: payment.recipientName,
        recipientIban: payment.recipientIban,
        purpose: payment.purpose,
      }));

      const { data: response } = await initiatePayment({
        variables: {
          tenantId,
          paymentIds: paymentIds,
          bankAccountId: input.bankAccountId,
          bankConnectionId: input.bankConnectionId,
          payments,
        },
      });

      if (showToasts) {
        toast.dismiss(toastId);
      }

      // TODO: Handle multiple payments
      return response.initiatePayment[0];
    } catch (error) {
      toast.dismiss(toastId);
      throw error;
    }
  };

  return {
    initiatePayment: handleInitiatePayment,
    loading,
    error,
    data: data?.initiatePayment,
  };
};

export const INITIATE_PAYMENT_MUTATION = gql`
  mutation InitiatePayment(
    $paymentIds: [String!]!
    $bankAccountId: String!
    $bankConnectionId: String!
    $tenantId: String!
    $payments: [PaymentInput!]!
  ) {
    initiatePayment(
      paymentIds: $paymentIds
      bankAccountId: $bankAccountId
      bankConnectionId: $bankConnectionId
      tenantId: $tenantId
      payments: $payments
    ) {
      id
      url
    }
  }
`;
